import Dropdown, { Option } from "@/src/components/tailwind/Dropdown";
import {
  DealRolePermissions,
  DealRoleType,
  useDealRolesQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useEffect, useMemo } from "react";
import { DealPermissionsSummary } from "./DealPermissionsSummary";

export type DealRoleOption = {
  id: string;
  name: string;
  description: string;
  type: DealRoleType;
  permissionLevels: DealRolePermissions;
};

function roleToOption(role: DealRoleOption): Option {
  return {
    value: role.id,
    label: role.name,
    subtitle: (
      <DealPermissionsSummary
        permissions={role.permissionLevels}
        roleType={role.type}
      />
    ),
  };
}

export function DealRolesDropdown(props: {
  dealId: string;
  roleType: DealRoleType;
  value?: string;
  onChange: (role: DealRoleOption) => void;
  label?: string;
  size?: "xs" | "s" | "m";
}) {
  const client = useGqlClient();
  const dealRolesQuery = useDealRolesQuery(
    client,
    {
      dealId: props.dealId,
      input: {
        roleTypes: [props.roleType],
        resourceTypes: [],
      },
    },
    {
      enabled: props.dealId !== "",
    },
  );

  const roles: DealRoleOption[] = useMemo(() => {
    if (!dealRolesQuery.data) {
      return [];
    }
    return dealRolesQuery.data.dealRoles.map((role) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      type: role.type,
      permissionLevels: role.permissionLevels,
    }));
  }, [dealRolesQuery.data]);

  const options: Option[] = roles.map(roleToOption);

  const selected =
    roles.find((role) => role.id === props.value) ?? roles[0] ?? null;

  useEffect(() => {
    if (!dealRolesQuery.data || roles.length === 0) {
      return;
    }
    const match = props.value
      ? roles.find((role) => role.id === props.value)
      : undefined;
    props.onChange(match ?? roles[0]);
    // Sync the resolved role to the parent once roles have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealRolesQuery.data, roles]);

  return (
    <Dropdown
      label={props.label}
      options={options}
      size={props.size}
      selectedOption={selected ? roleToOption(selected) : undefined}
      onSelect={(option) => {
        const role = roles.find((r) => r.id === option.value);
        if (role) {
          props.onChange(role);
        }
      }}
    />
  );
}
